import { Close } from "@winamp/icons/Close.js";
import { Loading } from "@winamp/icons/Loading.js";
import type { Upload } from "@winamp/media-upload/schema.js";
import type { useUploads } from "@winamp/media-upload/use-uploads.js";
import type { AvatarProps } from "@winamp/ui/components/Avatar/Avatar.js";
import { Badge } from "@winamp/ui/components/Badge/Badge.js";
import { Button } from "@winamp/ui/components/Button/Button.js";
import { ProgressCircle } from "@winamp/ui/components/ProgressCircle/ProgressCircle.js";
import { classNames } from "@winamp/ui/utils/classNames.js";

interface IMediaUploadStatusProps {
  upload?: Upload;
  cancelUpload?: ReturnType<typeof useUploads>["cancelUpload"];
  removeUpload?: ReturnType<typeof useUploads>["removeUpload"];
  shape?: AvatarProps["shape"];
  className?: string;
  loading?: boolean;
  cancelable?: boolean;
}

export function MediaUploadStatus({
  upload,
  removeUpload,
  cancelUpload,
  shape = "square",
  loading,
  cancelable = true,
  className,
}: IMediaUploadStatusProps) {
  if (loading) {
    return (
      <div
        className={classNames(
          "absolute z-10 inset-0 flex items-center justify-center bg-surface-overlay",
          {
            "[clip-path:polygon(var(--winamp-polygon))]": shape === "brand",
            "rounded-full": shape === "circle",
            rounded: shape === "square",
          },
          className,
        )}
      >
        <div className="relative size-10 bg-white rounded-full overflow-hidden">
          <Loading
            className={"animate-spin size-11 -ml-0.5 -mt-0.5 text-secondary"}
          />
          <Button
            icon
            variant="ghost"
            onClick={(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
              e.stopPropagation();
              cancelUpload?.(upload?.uploadId);
            }}
            className={classNames(
              "absolute inset-0 self-center justify-self-center rounded-full dark:text-black",
              {
                hidden: !cancelable,
              },
            )}
          >
            <Close />
          </Button>
        </div>
      </div>
    );
  }

  if (!upload || upload.status === "completed") {
    return null;
  }

  if (upload.status === "error") {
    return (
      <div
        className={classNames(
          "absolute z-10 inset-0 flex items-center justify-center bg-surface-overlay",
          {
            "[clip-path:polygon(var(--winamp-polygon))]": shape === "brand",
            "rounded-full": shape === "circle",
            "rounded-xl": shape === "square",
          },
          className,
        )}
      >
        <Badge color="red" className="w-fit justify-self-center">
          {upload.reason}

          <Close
            onClick={(e) => {
              e.stopPropagation();
              removeUpload?.(upload.uploadId);
            }}
            className={classNames({ hidden: !cancelable })}
          />
        </Badge>
      </div>
    );
  }

  return (
    <div
      className={classNames(
        "absolute z-10 inset-0 flex items-center justify-center bg-surface-overlay",
        {
          "[clip-path:polygon(var(--winamp-polygon))]": shape === "brand",
          "rounded-full": shape === "circle",
          "rounded-xl": shape === "square",
        },
        className,
      )}
    >
      <div className="relative size-10 bg-white rounded-full overflow-hidden">
        <ProgressCircle
          progress={"progress" in upload ? upload.progress : 0}
          className="size-full stroke-[8px] scale-[1.03]"
        />
        <div className="size-full absolute top-0 left-0 flex justify-center items-center">
          <Button
            icon
            variant="ghost"
            onClick={(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
              e.stopPropagation();
              cancelUpload?.(upload.uploadId);
            }}
            className={classNames("rounded-full dark:text-black", {
              hidden: !cancelable,
            })}
          >
            <Close />
          </Button>
        </div>
      </div>
    </div>
  );
}
